import type { components } from "./gen/schema";

/**
 * RFC 7807 `application/problem+json` — the ONE error shape the API returns, on every
 * surface, for every non-2xx. The type comes from the spec; we never redeclare it.
 */
export type Problem = components["schemas"]["Problem"];
export type ValidationError = components["schemas"]["ValidationError"];

/** Anything openapi-fetch hands us in its `error` slot, plus the failures it cannot model. */
export class ApiError extends Error {
  readonly problem: Problem;

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = "ApiError";
    this.problem = problem;
  }

  get status(): number {
    return this.problem.status;
  }

  /** Field-level failures, keyed by the field they belong to, ready to hang off an input. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const v of this.problem.validation_errors ?? []) {
      // First failure per field wins — showing three messages under one input is noise.
      if (!(v.field in out)) out[v.field] = v.message;
    }
    return out;
  }

  /** True when the problem is about the request as a whole, not a specific field. */
  get isFormLevel(): boolean {
    return (this.problem.validation_errors ?? []).length === 0;
  }
}

function isProblem(value: unknown): value is Problem {
  if (typeof value !== "object" || value === null) return false;
  const p = value as Record<string, unknown>;
  return (
    typeof p["type"] === "string" &&
    typeof p["title"] === "string" &&
    typeof p["status"] === "number"
  );
}

/**
 * Normalise whatever came back into an ApiError. A transport failure (server down, DNS,
 * CORS) has no problem body at all — we synthesise one so every consumer downstream can
 * assume the shape. Callers never branch on "was there a body".
 */
export function toApiError(error: unknown, response?: Response): ApiError {
  if (error instanceof ApiError) return error;

  if (isProblem(error)) return new ApiError(error);

  const status = response?.status ?? 0;
  return new ApiError({
    type: "https://docs.tankly.io/errors/transport",
    title: status === 0 ? "Network Error" : "Unexpected Error",
    status: status === 0 ? 0 : status,
    detail: error instanceof Error ? error.message : undefined,
  });
}

/** The status codes the UI reacts to structurally, rather than just rendering. */
export const isUnauthorized = (e: unknown) => e instanceof ApiError && e.status === 401;
export const isForbidden = (e: unknown) => e instanceof ApiError && e.status === 403;
export const isNotFound = (e: unknown) => e instanceof ApiError && e.status === 404;
